import { provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, SecurityContext, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { Router, provideRouter } from '@angular/router';
import { TranslocoHttpLoader } from '@app/transloco.module';
import { routes } from './app.routes';
import { ErrorHandlerService } from './services/error-handler.service';
import { environment } from '@env';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import * as Sentry from '@sentry/angular';
import { TokenizerAndRendererExtension, Tokens, marked } from 'marked';
import { CLIPBOARD_OPTIONS, ClipboardButtonComponent, MARKED_OPTIONS, MarkedRenderer, provideMarkdown } from 'ngx-markdown';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { QiraPreset } from 'styles/primeNg.preset';

// Adding highlights with ==abc== syntax to markdown renderer
interface HighlightToken {
  type: 'highlight';
  raw: string;
  text: string;
}

const highlightExtension: TokenizerAndRendererExtension = {
  name: 'highlight',
  level: 'inline',
  start(src: string): number | undefined {
    return src.indexOf('==') >= 0 ? src.indexOf('==') : undefined;
  },
  tokenizer(src: string): HighlightToken | undefined {
    const match = /^==([^=]+)==/.exec(src);
    if (match) {
      return {
        type: 'highlight',
        raw: match[0],
        text: match[1].trim(),
      };
    }
    return undefined;
  },
  renderer(token: Tokens.Generic): string {
    const highlight = token as HighlightToken;
    return `<mark>${highlight.text}</mark>`;
  },
};

marked.use({ extensions: [highlightExtension] });

export function getCustomRenderer(): MarkedRenderer {
  const renderer = new MarkedRenderer();

  const placeholderImg = 'assets/images/image_error_placeholder.png';

  renderer.image = ({ href, title, text }) => {
    return `
      <img src="${href}"
           alt="${text}"
           title="${title || ''}"
           onerror="this.src='${placeholderImg}'; this.onerror=null;">
    `;
  };
  return renderer;
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(),
    provideMarkdown({
      sanitize: SecurityContext.NONE,
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: {
          gfm: true,
          breaks: true,
          pedantic: false,
          renderer: getCustomRenderer(),
        },
      },

      clipboardOptions: {
        provide: CLIPBOARD_OPTIONS,
        useValue: {
          buttonComponent: ClipboardButtonComponent,
        },
      },
    }),
    provideTransloco({
      config: {
        availableLangs: [...environment.availableLanguages],
        defaultLang: environment.defaultLang,
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat({
      locales: environment.availableLanguages,
    }),
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    providePrimeNG({
      theme: {
        preset: QiraPreset,
        options: {
          darkMode: false,
          darkModeSelector: '.any-specific-class-that-we-not-use-yet',
        },
      },
      ripple: false,
    }),
    MessageService,
  ],
};
